import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { useState } from "react";

import { CreateFolderDialog, UserType } from "@fronterahealth/frontera-ui-components";

import {
  CreateGoalBankGoalsInput,
  GoalBankInput,
  ReportGoalType,
  ShortTermTimelineEstimationEnums,
  useCreateGoalBankGoalsMutation,
} from "@api/graphql/types-and-hooks";
import { FolderManagementAction } from "@components/GoalsBank/constants";
import { FolderManagementDialog } from "@components/GoalsBank/dialogs/FolderManagementDialog";
import { useGoalBankFolders } from "@components/GoalsBank/hooks/useGoalBankFolders";
import {
  findFolderSelection,
  getFolderSelections,
  getOrgFolderSection,
  getUserFolderSection,
} from "@components/GoalsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { convertDBString } from "@components/forms/utils";
import { notifySuccess } from "@components/notifications/notifications";

interface GoalBankShortTermGoalsSaveToFolderProps {
  onCloseClick: () => void;
  open: boolean;
  refetch: () => void;
  aiShortTermGoals: ReportGoalType[];
  shortTermGoals: ReportGoalType[];
}

export const GoalBankShortTermGoalsSaveToFolder: React.FC<GoalBankShortTermGoalsSaveToFolderProps> = ({
  onCloseClick,
  open,
  refetch,
  aiShortTermGoals,
  shortTermGoals,
}) => {
  const { user } = useAuth0();
  const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
  const userType: UserType = !!user?.org_id ? "organization" : "selfServe";
  const createGoalBankGoals = useCreateGoalBankGoalsMutation({});
  const { userFolders, orgFolders, createNewFolder } = useGoalBankFolders();
  const userFoldersSection = getUserFolderSection(userFolders);
  const orgFoldersSection = getOrgFolderSection(orgFolders, orgName);
  const [openNewFolderDialog, setNewFolderDialogOpen] = useState<boolean>(false);

  const getGoalBankInput = (goal: ReportGoalType) => {
    return {
      description: goal.description,
      goalName: goal.goalName,
      masteryCriteria: goal.masteryCriteria,
      programGoal: goal.programGoal,
      timelineEstimationType: goal.timelineEstimationType
        ? (convertDBString(goal.timelineEstimationType) as ShortTermTimelineEstimationEnums)
        : null,
      timelineEstimationValue: goal.timelineEstimationValue,
      assessmentToolSource: goal.assessmentToolSource,
      aiGenerated: goal.aiGenerated,
    } as GoalBankInput;
  };

  const createAiShortTermGoalMutation = (folderId: string, aiShortTermGoal: ReportGoalType) => {
    createGoalBankGoals.mutateAsync(
      {
        input: {
          shorttermGoalsData: [getGoalBankInput(aiShortTermGoal)],
          folderId,
        },
      },
      {
        onSuccess: () => {
          onCloseClick();
          notifySuccess("Saved To Folder");
          refetch();
          reset();
        },
      },
    );
  };

  const createReportGoalMutation = (folderId: string, shortTermGoal: ReportGoalType) => {
    createGoalBankGoals.mutateAsync(
      {
        input: {
          shorttermGoalsData: [getGoalBankInput(shortTermGoal)],
          folderId,
        },
      },
      {
        onSuccess: () => {
          onCloseClick();
          notifySuccess("Saved To Folder");
          refetch();
          reset();
        },
      },
    );
  };

  const { formState, RegisteredFormSelected, reset, onSubmit, watch } = useFormUtils<CreateGoalBankGoalsInput>({
    mutationFn: async (params) => {
      if (!params.folderId) {
        console.error("Missing folderId");
        return;
      }
      aiShortTermGoals.forEach(
        async (aiShortTermGoal) => await createAiShortTermGoalMutation(params.folderId, aiShortTermGoal),
      );
      shortTermGoals.forEach(async (shortTermGoal) => await createReportGoalMutation(params.folderId, shortTermGoal));
    },
  });
  const folderIdWatch = watch("folderId");

  const foldersSelection = getFolderSelections(userFoldersSection, orgFoldersSection);
  const placeholderText = () => {
    const selection = findFolderSelection(
      folderIdWatch ?? userFolders.at(0)?.id ?? orgFolders.at(0)?.id,
      foldersSelection,
    );
    return selection ? selection.primary : "Select a folder";
  };

  return (
    <FolderManagementDialog
      action={FolderManagementAction.SAVE}
      open={open}
      onCancelClick={() => {
        reset();
        onCloseClick();
      }}
      onSaveClick={onSubmit}
      dataTestID="add-short-term-goals-dialog"
    >
      <FormContainer onSubmit={onSubmit}>
        <RegisteredFormSelected
          formKey="folderId"
          required
          formState={formState}
          items={foldersSelection}
          title={"Select Folder "}
          placeholderText={placeholderText()}
          actionItem={{
            label: "Create New",
            onClick: () => {
              setNewFolderDialogOpen(true);
            },
          }}
        />
      </FormContainer>
      <CreateFolderDialog
        open={openNewFolderDialog}
        setOpen={setNewFolderDialogOpen}
        userType={userType}
        onCreateFolder={createNewFolder}
      />
    </FolderManagementDialog>
  );
};
